import React from 'react';
import { motion } from 'framer-motion';
import { Euro, Clock, Handshake, Users, Target, Award, Building2, Search } from 'lucide-react';

const BenefitsSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-secondary uppercase tracking-wider font-semibold">I Nostri Vantaggi</span>
          <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">Perché Vendere con Noi</h2>
          <p className="text-gray-600 text-lg">
            Dall'esperienza sul territorio alla tecnologia più avanzata, offriamo un servizio
            completo che ti garantisce una vendita vantaggiosa e senza stress.
          </p>
        </div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-10"
        >
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-primary hover:transform hover:-translate-y-2 transition-all duration-300"
          >
            <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Euro className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-primary">Massimizzazione del Valore</h3>
            <p className="text-gray-600">
              Valutiamo il tuo immobile con precisione, analizzando il mercato locale per 
              determinare il prezzo ottimale che massimizzi il ritorno e minimizzi i tempi di vendita.
            </p>
          </motion.div>
          
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-secondary hover:transform hover:-translate-y-2 transition-all duration-300"
          >
            <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Target className="h-8 w-8 text-secondary" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-secondary">Marketing Professionale</h3>
            <p className="text-gray-600">
              Servizio fotografico professionale, virtual tour 3D, planimetrie e pubblicazione 
              su tutti i principali portali immobiliari con annunci ottimizzati.
            </p>
          </motion.div>
          
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-primary hover:transform hover:-translate-y-2 transition-all duration-300"
          >
            <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-primary">Database di Acquirenti</h3>
            <p className="text-gray-600">
              Accesso immediato alla nostra esclusiva rete di acquirenti pre-qualificati, 
              accelerando il processo di vendita senza compromettere il valore dell'immobile.
            </p>
          </motion.div>
        </motion.div>
        
        {/* Additional benefits */}
        <div className="grid md:grid-cols-4 gap-6 mt-16">
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-primary/5 p-6 rounded-lg"
          >
            <Clock className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-primary">Tempi Rapidi</h3>
            <p className="text-gray-600">
              Concludiamo le vendite in metà del tempo della media di mercato grazie alla nostra rete e al nostro metodo.
            </p>
          </motion.div>
          
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-secondary/5 p-6 rounded-lg"
          >
            <Handshake className="h-12 w-12 text-secondary mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-secondary">Zero Pensieri</h3>
            <p className="text-gray-600">
              Gestione completa di ogni aspetto della vendita, dalla valutazione alla firma dal notaio.
            </p>
          </motion.div>
          
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-primary/5 p-6 rounded-lg"
          >
            <Award className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-primary">Consulenza Legale</h3>
            <p className="text-gray-600">
              Supporto legale dedicato per contratti, pratiche catastali e documentazioni necessarie.
            </p>
          </motion.div>
          
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-secondary/5 p-6 rounded-lg"
          >
            <Search className="h-12 w-12 text-secondary mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-secondary">Trasparenza Totale</h3>
            <p className="text-gray-600">
              Comunicazione chiara e costante durante tutto il processo, con report settimanali sui progressi.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;